/**
 * The Manager dashboard proper — one row per person in the resolved scope.
 *
 * Master §14.2 asks for direct reports, their current task, their workload and the
 * idle anomaly. Four of those are here; the fifth is a row in `ExceptionList`, and
 * each column is built from a source that publishes it rather than from something
 * adjacent that looks similar.
 *
 * Wave 1 retitled it *Ai cần giúp* and let `OverviewScreen` order it by need. The
 * ranking reads only the columns printed in the row, and the rule is printed above
 * the table: an order a reader cannot check against what they can see is a hidden
 * score, and this product does not ship one.
 *
 * ── What is deliberately not in a column ────────────────────────────────────
 *
 * No application name, no domain, no window title, no URL. `TimelineBucket` carries
 * `top_apps` and `top_domains` and `DailyAggregateRow` does not declare them, so
 * there is no field here to read — `web/shapes/overview.md` §4.2 and the guard in
 * `overview-privacy.test.tsx`. A task title is a work assignment the employee typed
 * or picked, and it is already on their own screen; an application name beside a
 * named individual on an aggregate page is the thing spec 01 §12 forbids.
 *
 * ── Three columns that can be absent, and none of them is a zero ────────────
 *
 *   presence   blank for `no_activity_data` — the aggregate observed nothing, and
 *              the attendance badge in the column beside it is where "day off" and
 *              "machine said nothing" are told apart. Never the word "offline":
 *              that is a claim about a machine and this row has no machine data.
 *   task       "no timer running" when the timers read succeeded and found none;
 *              "timers not read" when it failed. Two different sentences, because
 *              one is a fact about the person and one is a fact about this page.
 *   hours      "chưa tính" when the person has no day row in the covered window.
 *              Not `0 g` — `formatDurationSeconds` renders zero as `0 ph` and is
 *              documented to, so the branch that would reach it is not written.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import { formatDurationSeconds } from '../../timer/elapsed';
import type { AttendanceGridDay, TimeEntry } from '../../api/types';
import { attendanceDrillDownPath } from '../../api/paths';
import type { EmployeeTotals, PresenceReading } from '../../overview/fold';
import { EmptyState } from '../../states/EmptyState';
import { StatusBadge } from '../attendance/StatusBadge';
import { PRESENCE_LABEL } from './PresenceTile';

export interface ReportRow {
  readonly day: AttendanceGridDay;
  readonly presence: PresenceReading;
  readonly totals: EmployeeTotals | undefined;
  readonly running: TimeEntry | undefined;
}

export function ReportsTable({
  rows,
  total,
  timersRead,
  date,
  departmentId,
}: {
  readonly rows: readonly ReportRow[];
  /** `AttendanceGridResponse.total` — the whole scope, not this page. */
  readonly total: number;
  /** `false` when the running-timer read failed; the task column says which. */
  readonly timersRead: boolean;
  readonly date: string;
  readonly departmentId: string | null;
}): ReactNode {
  const { t } = useI18n();
  const labels = { hour: t('common.hourShort'), minute: t('common.minuteShort') };

  if (rows.length === 0) {
    return (
      <section className="wp-section" data-testid="k4-reports" aria-labelledby="k4-reports-title">
        <h2 className="wp-section__title" id="k4-reports-title">
          {t('overview.reports.title')}
        </h2>
        <EmptyState title={t('overview.presence.empty')} testId="k4-reports-empty" />
      </section>
    );
  }

  return (
    <section className="wp-section" data-testid="k4-reports" aria-labelledby="k4-reports-title">
      <h2 className="wp-section__title" id="k4-reports-title">
        {t('overview.reports.title')}
      </h2>
      {/* The order is a claim, so it is printed rather than left for the reader to
          reverse-engineer from the rows. */}
      <p className="wp-small wp-muted" data-testid="k4-reports-ordered">
        {t('overview.reports.ordered')}
      </p>
      {rows.length < total ? (
        <p className="wp-small wp-muted" data-testid="k4-reports-truncated">
          {t('overview.reports.truncated', { shown: rows.length, total })}
        </p>
      ) : null}
      <div className="wp-table-scroll">
        <table className="wp-table wp-reports-table">
          <thead>
            <tr>
              <th scope="col">{t('overview.reports.col.name')}</th>
              <th scope="col">{t('overview.reports.col.attendance')}</th>
              <th scope="col">{t('overview.reports.col.presence')}</th>
              <th scope="col">{t('overview.reports.col.task')}</th>
              <th scope="col" className="wp-num">
                {t('overview.reports.col.hours')}
              </th>
              <th scope="col">{t('overview.reports.col.drill')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.day.employee_id} data-testid={`k4-report-${row.day.employee_id}`}>
                <th scope="row">{row.day.employee_name}</th>
                <td className="wp-reports-table__status">
                  <StatusBadge status={row.day.attendance_status} />
                </td>
                <td
                  className="wp-reports-table__rest"
                  data-testid={`k4-report-presence-${row.day.employee_id}`}
                >
                  {row.presence === 'no_activity_data' ? null : t(PRESENCE_LABEL[row.presence])}
                </td>
                <td
                  className="wp-reports-table__rest"
                  data-testid={`k4-report-task-${row.day.employee_id}`}
                >
                  {row.running !== undefined
                    ? (row.running.task_title ?? row.running.achau_task_id ?? '')
                    : timersRead
                      ? t('overview.reports.noTask')
                      : t('overview.reports.taskUnavailable')}
                </td>
                <td
                  className="wp-num wp-reports-table__rest"
                  data-testid={`k4-report-hours-${row.day.employee_id}`}
                >
                  {row.totals === undefined
                    ? t('overview.reports.hoursUnknown')
                    : formatDurationSeconds(row.totals.onlineSeconds, labels)}
                </td>
                <td className="wp-reports-table__rest">
                  <Link
                    to={attendanceDrillDownPath({
                      date,
                      departmentId: departmentId ?? undefined,
                      employeeId: row.day.employee_id,
                    })}
                    data-testid={`k4-report-drill-${row.day.employee_id}`}
                  >
                    {t('overview.reports.drill', { name: row.day.employee_name })}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
